import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { format } from 'date-fns';

const AuctioneerDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAuctions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  const loadAuctions = async () => {
    if (!userProfile?.auctionHouseId) return;

    try {
      const auctionsRef = collection(db, 'auctions');
      const q = query(auctionsRef, where('auctionHouseId', '==', userProfile.auctionHouseId));
      const snapshot = await getDocs(q);
      
      const auctionsList: Auction[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        auctionsList.push({
          id: doc.id,
          ...data,
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Auction);
      });

      setAuctions(auctionsList);
    } catch (error) {
      console.error('Error loading auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Auctioneer Dashboard</h1>
          <p className="text-gray-600 mt-1">{userProfile?.auctionHouseName}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Active Auctions</h3>
            <span className="text-2xl">🔨</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">{auctions.length}</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Total Lots</h3>
            <span className="text-2xl">📦</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">
            {auctions.reduce((sum, a) => sum + a.totalLots, 0)}
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Total Predictions</h3>
            <span className="text-2xl">🎯</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">0</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Engagement</h3>
            <span className="text-2xl">📊</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">0%</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/auctioneer/create-auction" className="btn-primary text-center">
            Create New Auction
          </Link>
          <button className="btn-secondary">
            Import Lots (CSV)
          </button>
          <button className="btn-secondary">
            View Analytics
          </button>
        </div>
      </div>

      {/* Your Auctions */}
      <div className="card">
        <h3 className="text-xl font-bold mb-4">Your Auctions</h3>
        
        {loading ? (
          <p className="text-center py-8 text-gray-600">Loading auctions...</p>
        ) : auctions.length === 0 ? (
          <p className="text-gray-600 text-center py-8">
            No auctions yet. Click "Create New Auction" to get started!
          </p>
        ) : (
          <div className="space-y-4">
            {auctions.map((auction) => (
              <div key={auction.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-bold text-lg mb-1">{auction.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{auction.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>📦 {auction.totalLots} lots</span>
                      <span>📅 {format(new Date(auction.startDate), 'MMM dd, yyyy')}</span>
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">{auction.status}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/auctioneer/manage-bids/${auction.id}`}
                      className="btn-secondary text-sm"
                    >
                      📋 Manage Bids
                    </Link>
                    <Link
                      to={`/auctioneer/bulk-upload/${auction.id}`}
                      className="btn-secondary text-sm"
                    >
                      📁 Bulk Upload
                    </Link>
                    <Link
                      to={`/auctioneer/edit-auction/${auction.id}`}
                      className="btn-secondary text-sm"
                    >
                      Edit
                    </Link>
                    <Link
                      to={`/auctions/${auction.id}`}
                      className="btn-primary text-sm"
                    >
                      View
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuctioneerDashboard;